/*
  skm-v2-reviews: «Vis 5 til» i sections/skm-v2-reviews.liquid (utforming A).

  - De fem første er rendret av temaet. Side 2 og videre hentes fra Judge.me sitt offentlige
    widget-endepunkt (reviews_for_widget, GET, fem per side, nyeste først) og legges under de
    forrige, med telleren «Viser 10 av 121».
  - Neste side hentes først når kunden trykker. Knappen viser «Henter …» så lenge kallet går.
  - Ingen dobbeltvisning: anmeldelser som allerede står i lista hoppes over: samme uuid, eller
    samme navn, vurdering, tittel, dato og start av teksten.
  - Fordelingen fylles fra svaret hvis metafeltet manglet histogram. Da hentes side 1 når seksjonen
    nærmer seg skjermen, bare for fordelingen.
  - Feiler kallet når kunden trykker, skjules egen liste og Judge.me-widgeten vises som reserve.
  - Teksten fra Judge.me settes som ren tekst, aldri som HTML.
*/

/**
 * @typedef {{ uuid?: string, title?: string | null, rating?: number | string, body_html?: string | null,
 *   body?: string | null, created_at?: string, reviewer_name?: string | null,
 *   is_anonymous_reviewer?: boolean, reply_content?: string | null }} JmAnmeldelse
 * @typedef {{ rating: number | string, frequency: number | string }} JmHistogramRad
 * @typedef {{ reviews: JmAnmeldelse[], histogram?: JmHistogramRad[], pagination?: { total_pages?: number } }} JmSvar
 */

const PER_SIDE = 5;
const FORDELING_MARG = '600px 0px';
const DATO = new Intl.DateTimeFormat('nb-NO', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Europe/Oslo',
});
const ISO_DATO = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Oslo' });

/**
 * Reservenøkkel for anmeldelser uten uuid. Teksten tas uten mellomrom, så <br> og linjeskift gir samme nøkkel.
 * @param {unknown} navn
 * @param {unknown} vurdering
 * @param {unknown} tittel
 * @param {unknown} dato
 * @param {unknown} tekst
 */
const nokkel = (navn, vurdering, tittel, dato, tekst) =>
  [navn, vurdering, tittel, dato, String(tekst ?? '').replace(/\s+/g, '').slice(0, 80)]
    .map((v) => String(v ?? '').trim())
    .join('|');

/**
 * body_html fra Judge.me til ren tekst med linjeskift. DOMParser kjører ikke skript.
 * @param {unknown} html
 */
function tilTekst(html) {
  if (!html) return '';
  // Linjeskift i kilden er bare mellomrom i HTML. Det er <br> og <p> som bryter linja.
  const doc = new DOMParser().parseFromString(String(html).replace(/\s*\n\s*/g, ' '), 'text/html');
  doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  doc.querySelectorAll('p, div, li').forEach((el) => el.append('\n'));
  return (doc.body.textContent || '')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * @param {HTMLElement} el
 * @param {string} tekst
 */
function settTekst(el, tekst) {
  tekst.split('\n').forEach((linje, i) => {
    if (i) el.append(document.createElement('br'));
    el.append(linje);
  });
}

class SkmAnmeldelser extends HTMLElement {
  /** @type {HTMLUListElement | null} */ liste = null;
  /** @type {HTMLElement | null} */ mer = null;
  /** @type {HTMLButtonElement | null} */ knapp = null;
  /** @type {HTMLElement | null} */ teller = null;
  /** @type {HTMLElement | null} */ flammeMal = null;
  /** @type {Set<string>} */ uuider = new Set();
  /** @type {Set<string>} */ nokler = new Set();
  /** @type {IntersectionObserver | undefined} */ io;
  side = 1;
  totalt = 0;
  klar = false;
  ferdig = false;
  opptatt = false;

  connectedCallback() {
    if (this.klar) return;
    const mal = this.querySelector('template[data-flamme-mal]');
    this.liste = this.querySelector('ul[data-liste]');
    this.mer = this.querySelector('[data-mer]');
    this.knapp = this.querySelector('button[data-vis-flere]');
    this.teller = this.querySelector('[data-teller]');
    this.flammeMal = mal instanceof HTMLTemplateElement ? mal.content.querySelector('.skm-flammer') : null;
    this.totalt = Number(this.dataset.totalt) || 0;
    if (!this.liste || !this.flammeMal) return;
    if (!this.dataset.shop || !this.dataset.produkt) return;
    this.klar = true;

    this.liste.querySelectorAll('.skm-v2-reviews__review').forEach((li) => {
      if (li instanceof HTMLElement && li.dataset.uuid) this.uuider.add(li.dataset.uuid);
      this.nokler.add(this.nokkelFra(li));
    });
    this.side = Math.ceil(this.antallVist() / PER_SIDE);

    const fordeling = this.querySelector('[data-fordeling]');
    const manglerFordeling = fordeling instanceof HTMLElement && fordeling.hidden;
    if (this.knapp && this.mer) {
      this.knapp.addEventListener('click', () => this.visFlere());
      this.mer.hidden = false;
    }

    if (!manglerFordeling || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (inn) => {
        if (inn.some((e) => e.isIntersecting)) {
          io.disconnect();
          this.hent(1).catch(() => {});
        }
      },
      { rootMargin: FORDELING_MARG }
    );
    io.observe(this);
    this.io = io;
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  antallVist() {
    return this.liste ? this.liste.querySelectorAll('.skm-v2-reviews__review').length : 0;
  }

  /** @param {Element} li */
  nokkelFra(li) {
    const navn = li.querySelector('.skm-v2-reviews__reviewer')?.textContent;
    const vurdering = li.querySelector('.skm-flammer')?.getAttribute('aria-label')?.split(' ')[0];
    const tittel = li.querySelector('.skm-v2-reviews__review-title')?.textContent;
    const dato = li.querySelector('time')?.getAttribute('datetime');
    const tekst = li.querySelector(':scope > .skm-v2-reviews__body')?.textContent;
    return nokkel(navn, vurdering, tittel, dato, tekst);
  }

  /** @param {number} side */
  url(side) {
    const shop = this.dataset.shop || '';
    const p = new URLSearchParams({
      url: shop,
      shop_domain: shop,
      platform: 'shopify',
      product_id: this.dataset.produkt || '',
      page: String(side),
      per_page: String(PER_SIDE),
    });
    return `https://api.judge.me/reviews/reviews_for_widget?${p}`;
  }

  /**
   * @param {number} side
   * @returns {Promise<JmSvar>}
   */
  async hent(side) {
    const svar = await fetch(this.url(side), { credentials: 'omit' });
    if (!svar.ok) throw new Error(`Judge.me svarte ${svar.status}`);
    const data = await svar.json();
    if (!Array.isArray(data?.reviews)) throw new Error('Judge.me svarte uten anmeldelser');
    this.fyllFordeling(data.histogram);
    return data;
  }

  async visFlere() {
    const knapp = this.knapp;
    if (this.opptatt || !knapp) return;
    this.opptatt = true;
    knapp.setAttribute('aria-busy', 'true');
    const knappetekst = knapp.textContent;
    knapp.textContent = 'Henter …';
    try {
      /** @type {HTMLElement[]} */
      let nye = [];
      // Hopp over sider som bare gir dubletter (lista kan ha flyttet seg siden metafeltet ble skrevet).
      for (let forsok = 0; forsok < 3 && !nye.length && !this.ferdig; forsok++) {
        const side = this.side + 1;
        const data = await this.hent(side);
        this.side = side;
        const totaleSider = Number(data.pagination?.total_pages) || 0;
        if (!data.reviews.length || (totaleSider && side >= totaleSider)) this.ferdig = true;
        nye = this.leggTil(data.reviews);
      }
      knapp.textContent = knappetekst;
      this.oppdater(nye);
      if (!this.ferdig) this.oppdaterKnapp();
    } catch (feil) {
      this.visReserve();
    } finally {
      this.opptatt = false;
      knapp.removeAttribute('aria-busy');
    }
  }

  /** @param {JmAnmeldelse[]} anmeldelser */
  leggTil(anmeldelser) {
    /** @type {HTMLElement[]} */
    const nye = [];
    if (!this.liste) return nye;
    for (const a of anmeldelser) {
      const navn = a.is_anonymous_reviewer ? 'Anonym' : String(a.reviewer_name || '').trim() || 'Anonym';
      const vurdering = Math.max(0, Math.min(5, Number(a.rating) || 0));
      const tittel = String(a.title || '').trim();
      const tekst = tilTekst(a.body_html ?? a.body);
      const dato = a.created_at ? new Date(a.created_at) : null;
      const isoDato = dato && !Number.isNaN(dato.getTime()) ? ISO_DATO.format(dato) : '';
      const k = nokkel(navn, vurdering, tittel, isoDato, tekst);
      if ((a.uuid && this.uuider.has(a.uuid)) || this.nokler.has(k)) continue;
      if (a.uuid) this.uuider.add(a.uuid);
      this.nokler.add(k);
      const li = this.rad(a, navn, vurdering, tittel, tekst);
      this.liste.append(li);
      nye.push(li);
    }
    return nye;
  }

  /**
   * @param {JmAnmeldelse} a
   * @param {string} navn
   * @param {number} vurdering
   * @param {string} tittel
   * @param {string} tekst
   */
  rad(a, navn, vurdering, tittel, tekst) {
    const li = document.createElement('li');
    li.className = 'skm-v2-reviews__review';
    li.tabIndex = -1;
    if (a.uuid) li.dataset.uuid = a.uuid;

    const topp = document.createElement('div');
    topp.className = 'skm-v2-reviews__review-topp';
    const flammer = this.flammer(vurdering);
    if (flammer) topp.append(flammer);
    const dato = a.created_at ? new Date(a.created_at) : null;
    if (dato && !Number.isNaN(dato.getTime())) {
      const time = document.createElement('time');
      time.dateTime = ISO_DATO.format(dato);
      time.textContent = DATO.format(dato);
      topp.append(time);
    }
    li.append(topp);

    const p = document.createElement('p');
    p.className = 'skm-v2-reviews__reviewer';
    p.textContent = navn;
    li.append(p);

    if (tittel) {
      const h3 = document.createElement('h3');
      h3.className = 'skm-v2-reviews__review-title';
      h3.textContent = tittel;
      li.append(h3);
    }

    const brodtekst = document.createElement('p');
    brodtekst.className = 'skm-v2-reviews__body';
    settTekst(brodtekst, tekst);
    li.append(brodtekst);

    const svar = tilTekst(a.reply_content);
    if (svar) {
      const boks = document.createElement('div');
      boks.className = 'skm-v2-reviews__reply';
      const etikett = document.createElement('p');
      etikett.className = 'skm-v2-reviews__reply-label';
      etikett.textContent = 'Svar fra Superkraftmat';
      const kropp = document.createElement('p');
      kropp.className = 'skm-v2-reviews__body';
      settTekst(kropp, svar);
      boks.append(etikett, kropp);
      li.append(boks);
    }
    return li;
  }

  /**
   * Klon av skm-flammer fra temaet (fem fylte), med fyll per flamme satt etter vurderingen.
   * @param {number} vurdering
   */
  flammer(vurdering) {
    if (!this.flammeMal) return null;
    const fl = /** @type {HTMLElement} */ (this.flammeMal.cloneNode(true));
    fl.setAttribute('aria-label', `${vurdering} av 5 flammer`);
    fl.querySelectorAll('.skm-flammer__en').forEach((en, i) => {
      const prosent = Math.round(Math.max(0, Math.min(1, vurdering - i)) * 100);
      const fyll = en.querySelector('.skm-flammer__fyll');
      if (!(fyll instanceof HTMLElement)) return;
      if (prosent > 0) fyll.style.width = `${prosent}%`;
      else fyll.remove();
    });
    return fl;
  }

  /** @param {JmHistogramRad[] | undefined} histogram */
  fyllFordeling(histogram) {
    const ul = this.querySelector('[data-fordeling]');
    if (!(ul instanceof HTMLElement) || !ul.hidden || !this.flammeMal) return;
    if (!Array.isArray(histogram) || !histogram.length) return;
    const sum = histogram.reduce((s, r) => s + (Number(r.frequency) || 0), 0) || 1;
    const rader = [...histogram].sort((x, y) => Number(y.rating) - Number(x.rating));
    for (const r of rader) {
      const vurdering = Number(r.rating);
      const antall = Number(r.frequency) || 0;
      const li = document.createElement('li');
      li.className = 'skm-v2-reviews__fordeling-rad';

      const tall = document.createElement('span');
      tall.className = 'skm-v2-reviews__fordeling-tall';
      tall.setAttribute('aria-hidden', 'true');
      tall.textContent = String(vurdering);

      const fl = /** @type {HTMLElement} */ (this.flammeMal.cloneNode(true));
      fl.removeAttribute('role');
      fl.removeAttribute('aria-label');
      fl.setAttribute('aria-hidden', 'true');
      fl.style.setProperty('--fl', '12px');

      const bar = document.createElement('span');
      bar.className = 'skm-v2-reviews__fordeling-bar';
      bar.setAttribute('aria-hidden', 'true');
      const fyll = document.createElement('span');
      fyll.style.width = `${Math.round((antall * 100) / sum)}%`;
      bar.append(fyll);

      const ant = document.createElement('span');
      ant.className = 'skm-v2-reviews__fordeling-antall';
      ant.setAttribute('aria-hidden', 'true');
      ant.textContent = String(antall);

      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = `${vurdering} ${vurdering === 1 ? 'flamme' : 'flammer'}: ${antall} ${
        antall === 1 ? 'anmeldelse' : 'anmeldelser'
      }`;

      li.append(tall, fl, bar, ant, sr);
      ul.append(li);
    }
    ul.hidden = false;
  }

  /** @param {HTMLElement[]} nye */
  oppdater(nye) {
    const vist = this.antallVist();
    if (this.totalt && vist >= this.totalt) this.ferdig = true;
    if (this.teller) this.teller.textContent = `Viser ${vist} av ${Math.max(this.totalt, vist)}`;
    if (this.ferdig && this.knapp) {
      const hadde = document.activeElement === this.knapp;
      this.knapp.hidden = true;
      // Knappen forsvinner. Flytt fokus til den første nye anmeldelsen så tastaturet ikke mister plassen.
      if (hadde) nye[0]?.focus();
    }
  }

  oppdaterKnapp() {
    if (!this.knapp) return;
    const igjen = this.totalt - this.antallVist();
    const neste = Math.max(1, Math.min(PER_SIDE, igjen > 0 ? igjen : PER_SIDE));
    this.knapp.textContent = `Vis ${neste} til`;
  }

  visReserve() {
    const reserve = this.querySelector('[data-reserve]');
    if (!(reserve instanceof HTMLElement)) {
      if (this.knapp) {
        this.knapp.textContent = 'Kunne ikke hente flere';
        this.knapp.disabled = true;
      }
      return;
    }
    if (this.liste) this.liste.hidden = true;
    if (this.mer) this.mer.hidden = true;
    reserve.hidden = false;
  }
}

if (!customElements.get('skm-anmeldelser')) customElements.define('skm-anmeldelser', SkmAnmeldelser);
